import React, { useRef } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const ACCENT = '#ff7043';
const ACCENT_TEXT = '#1a0f0a';
const DEFAULT_PORT = '443';
const MENU_SIZE = 36;
const INDICATOR_SIZE = 20;
const ROW_PADDING = 10;
const ROW_GAP = 12;

// Первые буквы двух первых слов названия; слова без букв и цифр («&», «-») пропускаются
export function getMonogram(name) {
  const letters = String(name || '')
    .split(/\s+/)
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0])
    .filter(Boolean)
    .slice(0, 2);
  return letters.join('').toUpperCase() || '?';
}

// Порт 443 не показываем, нестандартный — «:порт»
export function getAddress(server) {
  const host = server.ip_address || '';
  const port = server.port === undefined || server.port === null ? '' : String(server.port);
  if (!host) return '';
  return port && port !== DEFAULT_PORT ? `${host}:${port}` : host;
}

function ServerList({ servers, activeServer, onServerClick, onServerMenu }) {
  const rowRefs = useRef([]);

  const safeServers = Array.isArray(servers)
    ? servers
    : Array.isArray(servers?.servers)
    ? servers.servers
    : [];

  if (!Array.isArray(servers)) {
    console.warn('⚠️ ServerList получил не массив:', servers);
  }

  if (safeServers.length === 0) {
    return (
      <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.55)' }}>
        Нет добавленных серверов
      </Typography>
    );
  }

  const activeIndex = safeServers.findIndex((server) => server.id === activeServer?.id);
  // Roving tabindex: в список входим на выбранной строке (или на первой, если выбора нет)
  const tabStopIndex = activeIndex >= 0 ? activeIndex : 0;

  const select = (server) => {
    if (server.id !== activeServer?.id) onServerClick(server);
  };

  const handleKeyDown = (event, index) => {
    const last = safeServers.length - 1;
    let next = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(safeServers[index]);
      return;
    }
    if (next === null) return;
    event.preventDefault();
    rowRefs.current[next]?.focus();
    select(safeServers[next]);
  };

  return (
    <Box
      role="radiogroup"
      aria-label="Серверы iiko"
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '2px' }}
    >
      {safeServers.map((server, index) => {
        const isActive = index === activeIndex;
        const name = server.server_name || server.name || server.ip_address;
        const address = getAddress(server);

        return (
          <Box
            key={server.id}
            sx={{
              position: 'relative',
              borderRadius: '10px',
              backgroundColor: isActive ? 'rgba(255,112,67,0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: isActive ? 'rgba(255,112,67,0.08)' : 'rgba(255,255,255,0.04)',
              },
              // Кнопка действий: на устройствах с мышью — по наведению/фокусу, на touch видна всегда
              '@media (hover: hover)': {
                '& .server-menu': { opacity: 0 },
                '&:hover .server-menu, & .server-menu:focus-visible': { opacity: 1 },
              },
            }}
          >
            <Box
              ref={(node) => {
                rowRefs.current[index] = node;
              }}
              role="radio"
              aria-checked={isActive}
              tabIndex={index === tabStopIndex ? 0 : -1}
              onClick={() => select(server)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              sx={{
                minHeight: 56,
                boxSizing: 'border-box',
                p: `${ROW_PADDING}px`,
                display: 'flex',
                alignItems: 'center',
                gap: `${ROW_GAP}px`,
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                outline: 'none',
                '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${ACCENT}` },
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 800,
                  backgroundColor: isActive ? ACCENT : 'rgba(255,255,255,0.06)',
                  color: isActive ? ACCENT_TEXT : '#a1a1aa',
                }}
              >
                {getMonogram(name)}
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap title={name} sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>
                  {name}
                </Typography>
                {address && (
                  <Typography noWrap sx={{ fontSize: '13px', color: isActive ? '#a1a1aa' : '#8e8e93' }}>
                    {address}
                  </Typography>
                )}
              </Box>

              {/* Место под кнопку «⋯», чтобы текст не уходил под неё */}
              {onServerMenu && <Box aria-hidden="true" sx={{ flexShrink: 0, width: MENU_SIZE }} />}

              <Box
                aria-hidden="true"
                sx={{
                  flexShrink: 0,
                  width: INDICATOR_SIZE,
                  height: INDICATOR_SIZE,
                  boxSizing: 'border-box',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(isActive
                    ? { backgroundColor: ACCENT, color: ACCENT_TEXT }
                    : { border: '1.5px solid rgba(255,255,255,0.2)' }),
                }}
              >
                {isActive && <CheckIcon sx={{ fontSize: 14 }} />}
              </Box>
            </Box>

            {onServerMenu && (
              <ButtonBase
                className="server-menu"
                aria-label={`Действия с сервером ${name}`}
                aria-haspopup="dialog"
                onClick={() => onServerMenu(server)}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: `${ROW_PADDING + INDICATOR_SIZE + ROW_GAP}px`,
                  transform: 'translateY(-50%)',
                  width: MENU_SIZE,
                  height: MENU_SIZE,
                  borderRadius: '10px',
                  color: '#a1a1aa',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff' },
                  '&.Mui-focusVisible': { outline: `2px solid ${ACCENT}` },
                }}
              >
                <MoreHorizIcon />
              </ButtonBase>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default ServerList;
